/**
 * Componente de Filtros e Ordenação para visualização mobile.
 * Exibido apenas em telas com largura <= 1200px (breakpoint da grid).
 * Oferece UX otimizada para toque: Drawer com seleção de coluna e aplicação de filtro/ordenação.
 */
import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import {
  Button,
  Drawer,
  Input,
  Typography,
  Divider,
  Icons,
  Tooltip,
} from '~/components';
import { Space } from 'antd';
import { TIPO_VISAO } from '~/modules/Helper/tipo-visao';
import { TIPO_COLUNA } from '~/modules/Helper/tipoColuna';
import {
  obterTipoLabelFiltro,
  obterDicaTipoFiltro,
} from '~/modules/Helper/filtroTipoCampoHelper';
import { AA_DISABLED_BORDER } from '~/modules/Helper/contrast.helper';
import * as S from './MobileGridFiltrosOrdenacao.styled';

const nativeSelectStyle = {
  width: '100%',
  minHeight: 40,
  borderRadius: 2,
  border: `1px solid ${AA_DISABLED_BORDER}`,
  color: '#000',
  padding: '8px 11px',
  background: '#fff',
  fontSize: 16,
};

const TIPOS_TEXTO = [
  TIPO_COLUNA.TEXTO_50,
  TIPO_COLUNA.TEXTO_100,
  TIPO_COLUNA.LINK,
  TIPO_COLUNA.BOOLEANO,
];
const TIPOS_NUMERO = [TIPO_COLUNA.INTEIRO, TIPO_COLUNA.DECIMAL];
const TIPOS_DATA = [TIPO_COLUNA.DATA];

function obterTipoInput(tipoValor) {
  if (TIPOS_NUMERO.includes(tipoValor)) return 'number';
  if (TIPOS_DATA.includes(tipoValor)) return 'date';
  return 'text';
}

function MobileGridFiltrosOrdenacao({
  configuracao,
  obterColunaChave,
  obterColunaLink,
  setDataFilterHeader,
  dataFilterHeader,
  setFiltro,
  filtro,
  setPagina,
  pagina,
  listaColunaOrdem = [],
  setListaColunaOrdem,
  normalizarListaColunaOrdem,
  removerColunaOrdenacao,
  obterOrdenacaoPadrao,
}) {
  const configuration = useSelector(state => state.configuration);
  const isMobile = useMediaQuery({ query: '(max-width: 1200px)' });
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('filtros'); // 'filtros' | 'ordenacao'

  // Estado para adicionar novo filtro
  const [colunaFiltroSelecionada, setColunaFiltroSelecionada] = useState(null);
  const [valorFiltro, setValorFiltro] = useState('');

  // Estado para adicionar nova ordenação
  const [colunaOrdenacaoSelecionada, setColunaOrdenacaoSelecionada] = useState(null);
  const [tipoOrdenacao, setTipoOrdenacao] = useState('ascend');

  const colunasFiltraveis = (configuracao?.VisaoColunas || []).filter(
    c => !c?.OcultoGridPortal && c?.FonteDados
  );

  const obterCampoColunaBusca = useCallback(() => {
    const colunaBusca =
      configuracao?.Tipo === TIPO_VISAO.AGRUPAMENTO
        ? obterColunaChave?.()
        : obterColunaLink?.();
    return colunaBusca?.FonteDados || null;
  }, [configuracao, obterColunaChave, obterColunaLink]);

  const obterNomeColuna = useCallback(
    campo => {
      const coluna = configuracao?.VisaoColunas?.find(c => c?.FonteDados === campo);
      return coluna?.NomeColuna || campo;
    },
    [configuracao]
  );

  const aplicarFiltro = useCallback(() => {
    if (!colunaFiltroSelecionada) return;
    const coluna = colunasFiltraveis.find(c => c.FonteDados === colunaFiltroSelecionada);
    if (!coluna) return;

    const valor = String(valorFiltro || '').trim();
    const campoBusca = obterCampoColunaBusca();

    if (coluna.FonteDados === campoBusca) {
      setFiltro(
        valor
          ? [{ Campo: coluna.FonteDados, Valor: valor, TipoValor: coluna.TipoValor }]
          : []
      );
    } else {
      setDataFilterHeader(prev => {
        const prevFiltros = Array.isArray(prev?.Filtros) ? prev.Filtros : [];
        const semEsta = prevFiltros.filter(f => f?.Campo !== coluna.FonteDados);
        const nextFiltros = valor
          ? [...semEsta, { Campo: coluna.FonteDados, Valor: valor, TipoValor: coluna.TipoValor }]
          : semEsta;
        return { ...(prev || {}), Filtros: nextFiltros };
      });
    }

    if (pagina !== 1) setPagina(1);
    setColunaFiltroSelecionada(null);
    setValorFiltro('');
  }, [
    colunaFiltroSelecionada,
    valorFiltro,
    colunasFiltraveis,
    obterCampoColunaBusca,
    setFiltro,
    setDataFilterHeader,
    setPagina,
    pagina,
  ]);

  const removerFiltro = useCallback(
    campo => {
      const campoBusca = obterCampoColunaBusca();
      if (campo === campoBusca) setFiltro([]);
      else {
        setDataFilterHeader(prev => {
          const prevFiltros = Array.isArray(prev?.Filtros) ? prev.Filtros : [];
          return { ...(prev || {}), Filtros: prevFiltros.filter(f => f?.Campo !== campo) };
        });
      }
      if (pagina !== 1) setPagina(1);
    },
    [obterCampoColunaBusca, setFiltro, setDataFilterHeader, setPagina, pagina]
  );

  const limparTodosFiltros = useCallback(() => {
    setFiltro([]);
    setDataFilterHeader(prev => ({ ...(prev || {}), Filtros: [] }));
    if (pagina !== 1) setPagina(1);
  }, [setFiltro, setDataFilterHeader, setPagina, pagina]);

  const aplicarOrdenacao = useCallback(() => {
    if (!colunaOrdenacaoSelecionada || !setListaColunaOrdem) return;

    const merged = [
      ...(listaColunaOrdem || [])
        .filter(item => item?.ColunaOrdem !== colunaOrdenacaoSelecionada)
        .map(i => ({ ColunaOrdem: i.ColunaOrdem, TipoOrdem: i.TipoOrdem })),
      { ColunaOrdem: colunaOrdenacaoSelecionada, TipoOrdem: tipoOrdenacao },
    ].map((item, idx) => ({ ...item, Ordem: idx + 1 }));

    setListaColunaOrdem(normalizarListaColunaOrdem?.(merged) ?? merged);
    setColunaOrdenacaoSelecionada(null);
    setTipoOrdenacao('ascend');
  }, [
    colunaOrdenacaoSelecionada,
    tipoOrdenacao,
    listaColunaOrdem,
    setListaColunaOrdem,
    normalizarListaColunaOrdem,
  ]);

  const restaurarOrdenacao = useCallback(() => {
    const colunaChave =
      configuracao?.Tipo === TIPO_VISAO.AGRUPAMENTO
        ? obterColunaChave?.()
        : obterColunaLink?.();
    if (colunaChave && obterOrdenacaoPadrao) obterOrdenacaoPadrao(colunaChave);
    setDataFilterHeader(prev => ({ ...(prev || {}), Filtros: [] }));
    setFiltro([]);
    if (pagina !== 1) setPagina(1);
  }, [
    configuracao,
    obterColunaChave,
    obterColunaLink,
    obterOrdenacaoPadrao,
    setDataFilterHeader,
    setFiltro,
    setPagina,
    pagina,
  ]);

  const filtrosColunaAtivos = Array.isArray(dataFilterHeader?.Filtros)
    ? dataFilterHeader.Filtros.filter(
        f =>
          f?.Campo &&
          f?.Valor !== null &&
          f?.Valor !== undefined &&
          f?.Valor !== ''
      )
    : [];

  const filtroBuscaAtivo =
    Array.isArray(filtro) &&
    filtro.length > 0 &&
    filtro[0]?.Campo &&
    filtro[0]?.Valor !== null &&
    filtro[0]?.Valor !== undefined &&
    filtro[0]?.Valor !== ''
      ? filtro[0]
      : null;

  const campoBusca = obterCampoColunaBusca();
  const filtrosAtivosMap = new Map();
  filtrosColunaAtivos.forEach(f => {
    if (f?.Campo) filtrosAtivosMap.set(f.Campo, f);
  });
  if (filtroBuscaAtivo?.Campo) filtrosAtivosMap.set(filtroBuscaAtivo.Campo, filtroBuscaAtivo);
  const filtrosAtivos = Array.from(filtrosAtivosMap.values());

  const ordenacoesAtivas = (listaColunaOrdem || [])
    .filter(o => o?.ColunaOrdem && o?.TipoOrdem)
    .slice()
    .sort((a, b) => (a?.Ordem ?? 9999) - (b?.Ordem ?? 9999));

  const colunaFiltro = colunasFiltraveis.find(c => c.FonteDados === colunaFiltroSelecionada);
  const tipoInputFiltro = colunaFiltro ? obterTipoInput(colunaFiltro.TipoValor) : 'text';

  const exibirComponente =
    isMobile &&
    configuracao &&
    (configuracao.Tipo === TIPO_VISAO.ANALITICO ||
      configuracao.Tipo === TIPO_VISAO.AGRUPAMENTO);

  if (!exibirComponente) return null;

  const renderConteudoFiltros = () => (
    <S.ConteudoSecao>
      <S.TituloSecao>
        <Typography.Title level={5}>Filtros</Typography.Title>
      </S.TituloSecao>

      <S.BlocoForm>
        <S.LabelLinhaComTipo>
          <Typography.Text strong>Adicionar filtro</Typography.Text>
          {colunaFiltroSelecionada && (
            <S.TipoCampoWrap>
              <S.TipoCampoLabel>Tipo do campo:</S.TipoCampoLabel>
              <S.TipoCampoValor>
                {obterTipoLabelFiltro(colunaFiltro?.TipoValor)}
              </S.TipoCampoValor>
              <Tooltip
                title={obterDicaTipoFiltro(colunaFiltro?.TipoValor)}
                overlayInnerStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  color: '#fff',
                }}
              >
                <S.TipoCampoIcone>
                  <Icons.InfoCircleOutlined aria-label="Informação sobre tipo do campo" />
                </S.TipoCampoIcone>
              </Tooltip>
            </S.TipoCampoWrap>
          )}
        </S.LabelLinhaComTipo>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <S.CampoRotulo>
            <S.CampoLabel htmlFor="mobile-filtro-coluna">
              Coluna para filtro
            </S.CampoLabel>
            <select
              id="mobile-filtro-coluna"
              value={colunaFiltroSelecionada || ''}
              onChange={event => {
                setColunaFiltroSelecionada(event.target.value || undefined);
                setValorFiltro('');
              }}
              style={nativeSelectStyle}
            >
              <option value="">Escolher coluna</option>
              {colunasFiltraveis.map(c => (
                <option key={c.FonteDados} value={c.FonteDados}>
                  {c.NomeColuna}
                </option>
              ))}
            </select>
          </S.CampoRotulo>

          {colunaFiltroSelecionada && (
            <>
              <S.CampoRotulo>
                <S.CampoLabel htmlFor="mobile-filtro-valor">
                  {`Buscar ${obterNomeColuna(colunaFiltroSelecionada)}`}
                </S.CampoLabel>
                <Input
                  id="mobile-filtro-valor"
                  placeholder={`Buscar ${obterNomeColuna(colunaFiltroSelecionada)}`}
                  value={valorFiltro}
                  onChange={e => setValorFiltro(e.target.value)}
                  type={tipoInputFiltro}
                  size="large"
                  style={{ width: '100%' }}
                />
              </S.CampoRotulo>
              <Button
                type="primary"
                block
                size="large"
                icon={<Icons.SearchOutlined />}
                onClick={aplicarFiltro}
              >
                Aplicar filtro
              </Button>
            </>
          )}
        </Space>
      </S.BlocoForm>

      {filtrosAtivos.length > 0 && (
        <>
          <Divider />
          <S.LabelSecao>
            <Typography.Text strong>Filtros ativos</Typography.Text>
          </S.LabelSecao>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {filtrosAtivos.map((item, idx) => (
              <S.CardItem key={`${item?.Campo}-${idx}`}>
                <div>
                  <Typography.Text strong>{obterNomeColuna(item?.Campo)}</Typography.Text>
                  <S.TextoSecundario>{String(item?.Valor ?? '')}</S.TextoSecundario>
                </div>
                <Button
                  type="text"
                  danger
                  icon={<Icons.CloseOutlined />}
                  onClick={() => removerFiltro(item?.Campo)}
                  aria-label={`Remover filtro de ${obterNomeColuna(item?.Campo)}`}
                />
              </S.CardItem>
            ))}
            <Button type="link" danger onClick={limparTodosFiltros} block>
              Limpar todos os filtros
            </Button>
          </Space>
        </>
      )}
    </S.ConteudoSecao>
  );

  const renderConteudoOrdenacao = () => (
    <S.ConteudoSecao>
      <S.TituloSecao>
        <Typography.Title level={5}>Ordenação</Typography.Title>
      </S.TituloSecao>

      <S.BlocoForm>
        <S.LabelSecao>
          <Typography.Text strong>Adicionar ordenação</Typography.Text>
        </S.LabelSecao>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <S.CampoRotulo>
            <S.CampoLabel htmlFor="mobile-ordenacao-coluna">
              Coluna para ordenação
            </S.CampoLabel>
            <select
              id="mobile-ordenacao-coluna"
              value={colunaOrdenacaoSelecionada || ''}
              onChange={event =>
                setColunaOrdenacaoSelecionada(event.target.value || undefined)
              }
              style={nativeSelectStyle}
            >
              <option value="">Escolher coluna</option>
              {colunasFiltraveis
                .filter(c => c.TipoValor !== TIPO_COLUNA.LINK)
                .filter(
                  c =>
                    !(listaColunaOrdem || []).some(
                      o => o?.ColunaOrdem === c.FonteDados
                    )
                )
                .map(c => (
                  <option key={c.FonteDados} value={c.FonteDados}>
                    {c.NomeColuna}
                  </option>
                ))}
            </select>
          </S.CampoRotulo>

          {colunaOrdenacaoSelecionada && (
            <>
              <S.CampoRotulo>
                <S.CampoLabel htmlFor="mobile-ordenacao-direcao">
                  Direção da ordenação
                </S.CampoLabel>
                <select
                  id="mobile-ordenacao-direcao"
                  value={tipoOrdenacao}
                  onChange={event => setTipoOrdenacao(event.target.value)}
                  style={nativeSelectStyle}
                >
                  <option value="ascend">Crescente (A → Z)</option>
                  <option value="descend">Decrescente (Z → A)</option>
                </select>
              </S.CampoRotulo>
              <Button
                type="primary"
                block
                size="large"
                icon={<Icons.OrderedListOutlined />}
                onClick={aplicarOrdenacao}
              >
                Aplicar ordenação
              </Button>
            </>
          )}
        </Space>
      </S.BlocoForm>

      {ordenacoesAtivas.length > 0 && (
        <>
          <Divider />
          <S.LabelSecao>
            <Typography.Text strong>Ordenação ativa</Typography.Text>
          </S.LabelSecao>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {ordenacoesAtivas.map((item, idx) => (
              <S.CardItem key={`${item?.ColunaOrdem}-${idx}`}>
                <div>
                  <Typography.Text strong>
                    {idx + 1}. {obterNomeColuna(item?.ColunaOrdem)}
                  </Typography.Text>
                  <S.TextoSecundario>
                    {item?.TipoOrdem === 'ascend' ? 'Crescente' : 'Decrescente'}
                  </S.TextoSecundario>
                </div>
                <Button
                  type="text"
                  danger
                  icon={<Icons.CloseOutlined />}
                  onClick={() => removerColunaOrdenacao?.(item?.ColunaOrdem)}
                  aria-label={`Remover ordenação de ${obterNomeColuna(item?.ColunaOrdem)}`}
                />
              </S.CardItem>
            ))}
            <Button type="link" onClick={restaurarOrdenacao} block>
              Restaurar ordenação padrão
            </Button>
          </Space>
        </>
      )}
    </S.ConteudoSecao>
  );

  const abrirDrawerComAba = aba => {
    setAbaAtiva(aba);
    setDrawerAberto(true);
  };

  return (
    <ThemeProvider theme={configuration}>
      <S.BarraAcoes>
        <S.BotaoAcao
          type="button"
          onClick={() => abrirDrawerComAba('filtros')}
        >
          <S.IconeBarra>
            <Icons.FilterOutlined aria-hidden />
          </S.IconeBarra>
          <span>Filtros</span>
          <S.BadgeBarra $ativo={filtrosAtivos.length > 0}>
            {filtrosAtivos.length > 0 ? filtrosAtivos.length : '—'}
          </S.BadgeBarra>
        </S.BotaoAcao>

        <S.BotaoAcao
          type="button"
          onClick={() => abrirDrawerComAba('ordenacao')}
        >
          <S.IconeBarra>
            <Icons.OrderedListOutlined aria-hidden />
          </S.IconeBarra>
          <span>Ordenação</span>
          <S.BadgeBarra $ativo={ordenacoesAtivas.length > 0}>
            {ordenacoesAtivas.length > 0 ? ordenacoesAtivas.length : '—'}
          </S.BadgeBarra>
        </S.BotaoAcao>
      </S.BarraAcoes>

      <Drawer
        title="Filtrar e Ordenar"
        placement="bottom"
        height="70vh"
        onClose={() => setDrawerAberto(false)}
        visible={drawerAberto}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ borderBottom: '1px solid #f0f0f0' }}
      >
        <S.DrawerTabs>
          <S.DrawerTab
            type="button"
            onClick={() => setAbaAtiva('filtros')}
            $ativa={abaAtiva === 'filtros'}
          >
            Filtros
            {filtrosAtivos.length > 0 && (
              <S.BadgeTab>{filtrosAtivos.length}</S.BadgeTab>
            )}
          </S.DrawerTab>
          <S.DrawerTab
            type="button"
            onClick={() => setAbaAtiva('ordenacao')}
            $ativa={abaAtiva === 'ordenacao'}
          >
            Ordenação
          </S.DrawerTab>
        </S.DrawerTabs>

        {abaAtiva === 'filtros' ? renderConteudoFiltros() : renderConteudoOrdenacao()}
      </Drawer>
    </ThemeProvider>
  );
}

export default MobileGridFiltrosOrdenacao;
